import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { useUserId } from '../../../app/hooks/accounts';
import {
  useSaveCollection,
  useSaveView,
  useUpdateCollection,
  useUpdateView,
} from '../../../app/hooks/view';
import { useTabManager } from '../../context/tab-manager-context';
import { storeViewState } from '../../utils/storage';
import { getTabDisplayName } from '../../utils/tab-display';
import { constructPersistedView } from '../../utils/view-state-helpers';
import { InfoCard } from '../alert-modal/styles';

import { FieldLabel } from './TabOptions';

import {
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  useToast,
} from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { ViewShareType } from '@controlrooms/models';

export const SaveCollectionModal = ({
  onClose,
  isUpdateCollection = false,
}: {
  onClose: () => void;
  isUpdateCollection?: boolean;
}) => {
  const {
    tabs,
    setViewCollection,
    viewCollection,
    loadCollection,
    getViewState,
    replaceTabIdWithViewId,
    updateTabMetadata,
  } = useTabManager();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentUserId = useUserId();
  const { mutateAsync: createCollection, error: saveError } = useSaveCollection();
  const { mutateAsync: updateCollection, error: updateError } = useUpdateCollection();
  const { mutateAsync: createView } = useSaveView();
  const { mutateAsync: updateView } = useUpdateView();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiError = (isUpdateCollection ? updateError : saveError) as AxiosError<any> | null;

  const {
    register,
    watch,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
  } = useForm<{
    name: string;
    description: string;
    shareType: ViewShareType;
    apiError: '';
  }>({
    mode: 'onChange',
    defaultValues: isUpdateCollection
      ? {
          name: viewCollection.name,
          description: viewCollection.description,
          shareType: ViewShareType.PRIVATE,
        }
      : {
          name: '',
          description: '',
          shareType: ViewShareType.PRIVATE,
          apiError: '',
        },
  });

  // const shareTypeOptions = useMemo(() => {
  //   return [
  //     {
  //       value: ViewShareType.PRIVATE,
  //       label: ViewShareType.PRIVATE,
  //     },
  //     {
  //       value: ViewShareType.EDITABLE,
  //       label: ViewShareType.EDITABLE,
  //     },
  //     {
  //       value: ViewShareType.VIEWABLE,
  //       label: ViewShareType.VIEWABLE,
  //     },
  //   ];
  // }, []);

  useEffect(() => {
    if (apiError?.response?.data?.detail) {
      const errorMessage =
        typeof apiError?.response?.data?.detail === 'string'
          ? apiError?.response?.data?.detail
          : 'Error occurred while saving changes';

      setError('apiError', { type: 'custom', message: errorMessage });
    }
  }, [apiError, setError]);

  const saveUnsavedTabs = useCallback(async (): Promise<string[]> => {
    const effectiveUserId = currentUserId ?? 0;

    // Update saved tabs that have unsaved changes (PUT)
    const savedDirtyTabs = tabs.filter(
      (t): t is typeof t & { viewId: string } => t.saved && !!t.viewId && !!t.isDirty,
    );
    for (const tab of savedDirtyTabs) {
      try {
        const viewState = await getViewState(tab.id);
        const persistView = constructPersistedView({
          viewState,
          viewName: tab.name || '',
          viewDescription: tab.description ?? '',
          currentUserId: effectiveUserId,
          shareType: (viewState.shareType ?? ViewShareType.PRIVATE) as ViewShareType,
          shared: viewState.shareType !== ViewShareType.PRIVATE,
        });
        await updateView({ view_id: tab.viewId, view: persistView });
        await storeViewState(tab.id, { ...viewState, isDirty: false });
        updateTabMetadata(tab.id, { isDirty: false });
      } catch (error) {
        console.error('Failed to update dirty view before collection save:', error);
        updateTabMetadata(tab.id, { isDirty: true });
      }
    }

    const unsavedViewTabs = tabs.filter((t) => !t.saved);
    const newlySavedViewIds: string[] = [];
    for (const tab of unsavedViewTabs) {
      try {
        const viewState = await getViewState(tab.id);
        const baseName = getTabDisplayName(tab);
        const displayName = `${baseName} ${Date.now()}`;
        const persistView = constructPersistedView({
          viewState,
          viewName: displayName,
          viewDescription: tab.description ?? '',
          currentUserId: effectiveUserId,
          shareType: ViewShareType.PRIVATE,
          shared: false,
        });
        const viewId = await createView(persistView);
        const updatedViewState = {
          ...viewState,
          view_id: viewId,
          viewId,
          name: displayName,
          description: tab.description ?? '',
          shareType: ViewShareType.PRIVATE,
          shared: false,
          isDirty: false,
        };
        await replaceTabIdWithViewId(
          tab.id,
          viewId,
          {
            saved: true,
            viewId,
            userId: effectiveUserId,
            name: displayName,
            description: tab.description ?? '',
            isDirty: false,
          },
          updatedViewState,
        );
        newlySavedViewIds.push(viewId);
      } catch (error) {
        console.error('Failed to save unsaved view before collection save:', error);
        updateTabMetadata(tab.id, { isDirty: true });
      }
    }

    const existingSavedViewIds = tabs
      .filter((t): t is typeof t & { viewId: string } => t.saved && !!t.viewId)
      .map((t) => t.viewId)
      .filter((id) => id !== 'default');

    return [...new Set([...existingSavedViewIds, ...newlySavedViewIds])];
  }, [
    currentUserId,
    tabs,
    getViewState,
    updateView,
    createView,
    replaceTabIdWithViewId,
    updateTabMetadata,
  ]);

  const handleUpdate = useCallback(
    async (name: string, description: string) => {
      const collection_id = viewCollection.collectionId;
      setIsSaving(true);
      try {
        const allViewIds = await saveUnsavedTabs();
        const viewIds = allViewIds.filter((id) => id !== 'default');

        if (viewIds.length === 0) {
          toast.error('You need to have tabs open to create a tab set.');
          return;
        }

        if (collection_id) {
          await updateCollection({
            collection_id: collection_id,
            collection: {
              collection_name: name || '',
              description: description || '',
              view_ids: viewIds,
            },
          });
          queryClient.refetchQueries(['user-collections', currentUserId]);
          queryClient.refetchQueries(['collection', collection_id]);
        }
        setViewCollection((prev) => ({
          ...prev,
          isDirty: false,
          name: name || '',
          description: description || '',
        }));
        onClose();
      } catch (error) {
        console.error(error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      currentUserId,
      onClose,
      queryClient,
      saveUnsavedTabs,
      setViewCollection,
      toast,
      updateCollection,
      viewCollection.collectionId,
    ],
  );

  const _createCollection = async (collection: { name: string; description: string }) => {
    if (tabs.length === 0) {
      toast.error('You need to have tabs open to create a tab set.');
      return;
    }
    setIsSaving(true);
    try {
      const allViewIds = await saveUnsavedTabs();
      const persistedCollection = {
        ...collection,
        viewsIds: allViewIds,
      };

      const response = await createCollection({
        collection_name: persistedCollection.name,
        description: persistedCollection.description,
        view_ids: persistedCollection.viewsIds,
      });
      setViewCollection((prev) => ({
        ...prev,
        name: response.collection_name,
        description: response.description,
        viewsIds: persistedCollection.viewsIds,
        isDirty: false,
        collectionId: response.collection_id,
        views: response.views || [],
      }));

      // Load the newly created collection
      await loadCollection({
        collection_id: response.collection_id,
        collection_name: response.collection_name,
        description: response.description,
        view_ids: persistedCollection.viewsIds,
        views: response.views || [],
      });

      queryClient.refetchQueries(['user-collections', currentUserId]);
      queryClient.refetchQueries(['collection', response.collection_id]);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateCollection = () => {
    const collectionName = watch(`name`);
    const collectionDescription = watch(`description`);
    if (!collectionName || !collectionDescription) {
      trigger();
      return;
    }
    if (isUpdateCollection) {
      handleUpdate(collectionName, collectionDescription);
    } else {
      _createCollection({ name: collectionName, description: collectionDescription });
    }
  };

  return (
    <Modal open={true} onCancel={onClose} size="sm">
      <ModalHeader title="Save new collection" />
      <ModalBody>
        <div style={{ padding: '10px', fontSize: '13px' }}>
          {errors?.apiError?.message && (
            <InfoCard type="error">
              <Icon className="icon" name={ICONS.SystemDown} />
              <span data-testid="alert-error">{errors['apiError']?.message}</span>
            </InfoCard>
          )}
          <p>Save the views you are currently using as a Collection.</p>
          <div>
            <FieldLabel>Name</FieldLabel>
            <TextInput
              type="string"
              onKeyDown={(e) => {
                clearErrors('apiError');
                e.stopPropagation();
              }}
              className="view-name"
              label=""
              placeholder=""
              errorId={'view-name-required'}
              disabled={false}
              {...register(`name`, {
                validate: (value) => {
                  if (value.length <= 1) {
                    return 'Provide a valid view name';
                  }
                  return true;
                },
              })}
              errorMessage={errors.name?.message || ''}
            />
            <br />
            <FieldLabel>Description</FieldLabel>
            <TextInput
              type="string"
              className="view-description"
              label=""
              onKeyDown={(e) => {
                clearErrors('apiError');
                e.stopPropagation();
              }}
              placeholder=""
              errorId={'view-description-required'}
              disabled={false}
              {...register(`description`, {
                validate: (value) => {
                  if (value.length <= 1) {
                    return 'Provide a valid view description';
                  }
                  return true;
                },
              })}
              errorMessage={errors.description?.message || ''}
            />
          </div>
          {/* TODO: Implement sharing in backend */}
          {/* <div style={{ marginTop: '10px' }}>
            <FieldLabel>Sharing</FieldLabel>
            <Select
              style={{ width: '300px' }}
              options={shareTypeOptions}
              markSelectedOption
              defaultOption={{
                label: watch(`shareType`),
                value: watch(`shareType`),
              }}
              onChange={(e) => {
                setValue('shareType', e.value as ViewShareType);
              }}
            />
            {watch(`shareType`) === ViewShareType.PRIVATE && (
              <>
                <p>This collection is primarily for your own use.</p>
                <p>
                  You can still use the Share action to copy a link. This takes a snapshot of the
                  current state that others can view, minus the name and description you have
                  supplied. Any changes you make after sharing the link will not be reflected to the
                  link recipients.
                </p>
              </>
            )}
            {watch(`shareType`) === ViewShareType.VIEWABLE && (
              <>
                <p>
                  Your colleagues will see this collection in their shared list, but any changes
                  they make to it will be visible only to them. Changes you make can be published to
                  all and applied by each viewer using the Update action.
                </p>
              </>
            )}
            {watch(`shareType`) === ViewShareType.EDITABLE && (
              <>
                <p>
                  You and your colleagues can edit the collection and publish those edits using the
                  Publish action. To apply someone elses edits to you will need to use the Update
                  action.
                </p>
              </>
            )}
          </div> */}
        </div>
      </ModalBody>
      <ModalFooter
        confirmText={isSaving ? 'Saving...' : 'Save Collection'}
        cancelText="Cancel"
        onConfirm={isSaving ? undefined : handleCreateCollection}
        confirmDisabled={isSaving}
        cancelTestId="modal-close-icon-delete-alert"
      />
    </Modal>
  );
};
